import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useGroups from "../../api/hooks/useGroups";
import useUsers from "../../api/hooks/useUsers";
import { lang } from "../../language/Strings";
import { ItemDescription } from "src/components/ItemDescription";
import Toggle from "src/components/Toggle";
import useMyUser from "src/api/hooks/useMyUser";
import { LocalStorageKeys } from "src/utils/localStorageKeys";
import useChangeTitle from "src/api/hooks/useChangeTitle";
import { routes } from "src/routes";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { objectTypes } from "src/consts/objectTypes";
import { InspectionResponse } from "src/types/InspectionUpdateRequest";

export function FilterPage() {
  const { companyId } = useParams();
  const { data: groups } = useGroups();
  const { data: users } = useUsers();
  const nav = useNavigate();
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    localStorage.setItem(
      LocalStorageKeys.filter,
      JSON.stringify(Object.fromEntries(formData.entries()))
    );
    nav(-1);
  }
  const lastPage = localStorage.getItem(LocalStorageKeys.lastPage);
  const [filter, setFilter] = useState<Record<string, string>>({});
  async function clearFilters() {
    localStorage.removeItem(LocalStorageKeys.filter);
    nav(-1);
  }
  const { data: inspections } = useProjectVariations();
  useEffect(() => {
    const filter = JSON.parse(
      localStorage.getItem(LocalStorageKeys.filter) || "{}"
    );
    setFilter(filter);
  }, []);
  const { data: myUser } = useMyUser();
  useChangeTitle(lang.filter);
  return (
    <div className="h-full w-full p-2">
      <form onSubmit={handleSubmit}>
        {myUser ? (
          <ItemDescription label={lang.show}>
            <Toggle
              falseLabel={lang.myAssignments}
              trueLabel={lang.allInspections}
              trueValue={""}
              falseValue={myUser?.id}
              name="assignmentsByCurrentUser"
              defaultValue={filter.assignmentsByCurrentUser || ""}
            />
          </ItemDescription>
        ) : (
          ""
        )}
        <ItemDescription label={lang.startDate}>
          <input type="date" name="startDate" className="" />
        </ItemDescription>
        <ItemDescription label={lang.endDate}>
          <input type="date" name="endDate" className="" />
        </ItemDescription>
        {!lastPage?.startsWith(
          routes.Egenkontroller.Pages.List.url(companyId)
        ) && (
          <ItemDescription label={lang.deviationType}>
            <select name="deviationType" className="border-2 border-Grey2 p-1">
              <option></option>
              <option value="Urgent">{lang.urgent}</option>
              <option value="Improvement">{lang.improvement}</option>
              <option value="Other">{lang.other}</option>
              <option value="Vandalism">{lang.vandalism}</option>
            </select>
          </ItemDescription>
        )}
        {lastPage?.startsWith(
          routes.Egenkontroller.Pages.List.url(companyId)
        ) && (
          <ItemDescription label={lang.moment}>
            <select
              name="information"
              className="border-2 border-Grey2 p-1 w-3/4"
            >
              {inspections
                ?.filter((i) => i.objectType === objectTypes.Inspection)
                .reduce((acc: string[], i: InspectionResponse) => {
                  if (!acc.includes(i.information || "")) {
                    acc.push(i.information || "");
                  }
                  return acc;
                }, [])
                .map((i) => (
                  <option key={`inspection-${i}`} value={i || ""}>
                    {i}
                  </option>
                ))}
            </select>
          </ItemDescription>
        )}
        <ItemDescription label={lang.groups}>
          {groups ? (
            <select
              name="groups"
              className="border-2 border-Grey2 p-1 w-3/4"
              multiple
              defaultValue={filter.groups || []}
            >
              {groups?.map((g) => (
                <option key={`group-${g.id}`} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          ) : (
            <span className="text-PrimaryRed">
              {lang.noGroupsMessage}{" "}
              <a href={`https://portal.glservices.se/${companyId}`}>
                {lang.userManagement}
              </a>
            </span>
          )}
        </ItemDescription>
        <ItemDescription label={lang.reportedByUser}>
          <select
            name="userId"
            className="border-2 border-Grey2 p-1 w-3/4"
            defaultValue={filter.userId || ""}
          >
            <option></option>
            {users?.map((g) => (
              <option key={`user-${g.id}`} value={g.id}>
                {g.firstName + " " + g.surname}
              </option>
            ))}
          </select>
        </ItemDescription>
        {!lastPage?.startsWith(
          routes.Egenkontroller.Pages.List.url(companyId)
        ) && (
          <ItemDescription label={lang.assignedTo}>
            <select
              name="assignedToUserId"
              className="border-2 border-Grey2 p-1 w-3/4"
              defaultValue={filter.assignedToUserId || ""}
            >
              <option></option>
              {users?.map((g) => (
                <option key={`user-${g.id}`} value={g.id}>
                  {g.firstName + " " + g.surname}
                </option>
              ))}
            </select>
          </ItemDescription>
        )}
        <ItemDescription label={lang.exitCategory}>
          <select
            name="exitCategory"
            className="border-2 border-Grey2 p-1 max-w-[80%]"
            defaultValue={filter.exitCategory || ""}
          >
            <option></option>
            <option value="Already exists">{lang.alreadyExists}</option>
            <option value="Included, sent to staff">{lang.included}</option>
            <option value="Ordered, workorder created">{lang.ordered}</option>
            <option value="Other">{lang.other}</option>
          </select>
        </ItemDescription>

        {!lastPage?.startsWith(
          routes.Egenkontroller.Pages.List.url(companyId)
        ) && (
          <ItemDescription label={lang.statusText}>
            <select
              name="status"
              className="border-2 border-Grey2 p-1"
              defaultValue={filter.status || ""}
            >
              <option></option>
              <option value="Contact customer">{lang.contactCustomer}</option>
              <option value="Created">{lang.created}</option>
              <option value="Finished">{lang.finished}</option>
              <option value="On Hold">{lang.onHold}</option>
              <option value="Sent to customer">{lang.sentToCustomer}</option>
              <option value="Sold">{lang.sold}</option>
            </select>
          </ItemDescription>
        )}
        {lastPage?.toLowerCase()?.includes("map") && (
          <ItemDescription label={lang.objectType}>
            <select
              name="objectType"
              className="border-2 border-Grey2 p-1"
              defaultValue={filter.objectType || ""}
            >
              <option></option>
              <option value="Inspection">{lang.Egenkontroller}</option>
              <option value="Deviation">{lang.deviations}</option>
            </select>
          </ItemDescription>
        )}
        <div className="w-full flex items-center justify-center mt-2">
          <button
            type="button"
            className="border-2 border-Grey2 pl-2 pr-2 pt-1 pb-1 rounded-lg "
            onClick={clearFilters}
          >
            {lang.clearFilters}
          </button>
          <button
            type="submit"
            className="border-2 border-Grey2 pl-2 pr-2 pt-1 pb-1 rounded-lg "
          >
            {lang.saveFilters}
          </button>
        </div>
      </form>
    </div>
  );
}
